import exportToCSV from "../utils/ExportExcel";
import { TruncateDecimals, loadFromStorage, useSaveToStorage } from "../utils";
import { useState, useMemo, useEffect } from "react"
import { useJobtrackMachineRecord } from "../scripts/queries/useMachineQueries";

import {useReactTable, getCoreRowModel, getSortedRowModel} from '@tanstack/react-table';

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];

export const useMachineNormalizer = (theme) => {

    const persisted = loadFromStorage('MACHINE_FILTERS') || {};
    const [search, setSearch] = useState(persisted.search || "");
    const [sorting, setSorting] = useState(persisted.sorting || []);
    const [debounceSearch, setDebounceSearch] = useState(persisted.search || "");

    useSaveToStorage('MACHINE_FILTERS', {search, sorting})
    const {data = EMPTY_ARRAY, refetch: fetchMachineData, isLoading: loading} = useJobtrackMachineRecord();

    useEffect(() => {
        const t = setTimeout(() => setDebounceSearch(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    const filteredData = useMemo(() => {
        const searchTerm = debounceSearch.toLowerCase();
        return data.filter(item => {
            const machine = (item.maquina || "").toLowerCase();
            return !searchTerm || machine.includes(searchTerm);
        })
    }, [data, debounceSearch])

    const columns = useMemo(() => [
        {accessorKey: 'maquina', header: 'Máquina'},
        {accessorKey: 'hrsArregloReal', header: 'H. arreglo', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'hrsProdReal', header: 'H. producción', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'hrsImprodReal', header: 'H. improd.', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'hrsImprodNoReportadas', header: 'H. improd. no reportadas', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'hrsMaquinaNoTripuladas', header: 'H. no tripuladas', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'hrsMaqRelojChecador', header: 'H. reloj checador', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'eficienciaMaquinaPonderada', header: 'Eficiencia ponderada', sortingFn: 'basic', cell: ({getValue}) => `${Math.round((getValue() || 0) * 100)}%`},
        {accessorKey: 'costoProductivo', header: 'Suma Costo Total', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
        {accessorKey: 'costoImproductivo', header: 'Suma Tiempo Improd', sortingFn: 'basic', cell: ({getValue}) => TruncateDecimals(getValue())},
    ], []);

    const table = useReactTable({
        data: filteredData,
        columns,
        state:{sorting},
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const exportCSV = () => {
        const csvRows = [];

        const headers = columns.map((col) => col.header);
        csvRows.push(headers);

        table.getSortedRowModel().rows.forEach((r) => {
            const row = r.original;
            const values = columns.map((col) => {
                const accessor = col.accessorKey;
                const value = row[accessor];

                switch (accessor) {
                    case 'eficienciaMaquinaPonderada':
                        return Math.round((value ?? 0) * 100)
                    case 'hrsArregloReal':
                    case 'hrsProdReal':
                    case 'hrsImprodReal':
                    case 'hrsImprodNoReportadas':
                    case 'hrsMaquinaNoTripuladas':
                    case 'hrsMaqRelojChecador':
                    case 'costoProductivo':
                    case 'costoImproductivo':
                        return value ?? 0;
                    default:
                        return value ?? "";
                }
            });
            csvRows.push(values);
        });

        exportToCSV(`Maquinas.xlsx`, csvRows)
    }

    return {table, loading, search, fetchMachineData, setSearch, exportCSV};
}
